//import { User } from "./User";
//import { Zone } from "./Zone";


/**
 * The Room class stores the properties of each server room.
 * This class is used internally by the {@link SmartFoxClient} class; also, Room objects are returned by various methods and events of the SmartFoxServer API.
 *
 * <b>NOTE</b>: in the provided examples, {@code room} always indicates a Room instance.
 *
 * @version	1.0.0
 *
 * @author	The gotoAndPlay() Team
 * 			{@link http://www.smartfoxserver.com}
 * 			{@link http://www.gotoandplay.it}
 */
export class Room {
    private id: number;
    private name: string;
    private maxUsers: number;
    private maxSpectators: number;
    private temp: boolean;
    private game: boolean;
    private priv: boolean;
    private limbo: boolean;
    private userCount: number;
    private specCount: number;

    private myPlayerIndex: number;

    private userList: any[];
    private variables: any[];

    /**
     * Room contructor.
     *
     * @param	id:				the room id.
     * @param	name:			the room name.
     * @param	maxUsers:		the maximum number of users that can join the room simultaneously.
     * @param	maxSpectators:	the maximum number of spectators in the room (for game rooms only).
     * @param	isTemp:			{@code true} if the room is temporary.
     * @param	isGame:			{@code true} if the room is a "game room".
     * @param	isPrivate:		{@code true} if the room is private (password protected).
     * @param	isLimbo:		{@code true} if the room is a "limbo room".
     * @param	userCount
     * @param	specCount
     *
     * @exclude
     */
    constructor(id: number, name: string, maxUsers: number, maxSpectators: number, isTemp: boolean, isGame: boolean, isPrivate: boolean, isLimbo: boolean, userCount: number = 0, specCount: number = 0) {
        this.id = id;
        this.name = name;
        this.maxSpectators = maxSpectators;
        this.maxUsers = maxUsers;
        this.temp = isTemp;
        this.game = isGame;
        this.priv = isPrivate;
        this.limbo = isLimbo;

        this.userCount = userCount;
        this.specCount = specCount;
        this.userList = [];
        this.variables = [];
    }

    /**
     * Add a user to the room.
     *
     * @param	u:	the {@link User} object.
     * @param	id:	the user id.
     *
     * @exclude
     */
    /*public addUser(u:User, id:number):void
    {
        this.userList[id] = u;

        if (this.game && u.isSpectator())
            this.specCount++;
        else
            this.userCount++;
    }
*/
    /**
     * Remove a user from the room.
     *
     * @param	id:	the user id.
     *
     * @exclude
     */
    /*public removeUser(id:number):void
    {
        let u:User = this.userList[id];

        if (this.game && u.isSpectator())
            this.specCount--;
        else
            this.userCount--;

        delete this.userList[id];
    }*/

    /**
     * Get the list of users currently inside the room.
     * As the returned list is an associative array with user id(s) as keys, in order to iterate it a <i>for-in</i> loop or a <i>for-each</i> loop should be used.
     *
     * @return	A list of {@link User} objects.
     *
     * @example	<code>
     * 			let users:Array = room.getUserList()
     *
     * 			for (let u:String in users)
       *				trace(users[u].getName())
     * 			</code>
     *
     * @see		#getUser
     * @see		User
     *
     * @version	SmartFoxServer Basic / Pro
     */
    public getUserList(): any[] {
        return this.userList;
    }

    /**
     * Retrieve a user currently in the room.
     *
     * @param 	userId:	the user name ({@code String}) or the id ({@code int}) of the user to retrieve.
     *
     * @return	A {@link User} object.
     *
     * @example	<code>
     * 			let user:User = room.getUser("jack")
     * 			</code>
     *
     * @see		#getUserList
     * @see		User
     *
     * @version	SmartFoxServer Basic / Pro
     */
    /*public getUser(userId:any):User
    {
        let user:User = null;

        if (typeof userId == "number")
        {
            user =  this.userList[userId];
        }

        else if (typeof userId == "string")
        {
            for (let i:string in this.userList)
            {
                let u:User = this.userList[i];

                if (u.getName() == userId)
                {
                    user = u;
                    break;
                }
            }
        }

        return user;
    }*/

    /**
     * Reset users list.
     *
     * @exclude
     */
    public clearUserList(): void {
        this.userList = [];
        this.userCount = 0;
        this.specCount = 0;
    }

    /**
     * Retrieve a Room Variable.
     *
     * @param	varName:	the name of the variable to retrieve.
     *
     * @return	The Room Variable's value.
     *
     * @example	<code>
     * 			let location:String = room.getVariable("location") as String
     * 			</code>
     *
     * @see		#getVariables
     * @see		SmartFoxClient#setRoomVariables
     *
     * @version	SmartFoxServer Basic / Pro
     */
    public getVariable(varName: string): any {
        return this.variables[varName];
    }

    /**
     * Retrieve the list of all Room Variables.
     *
     * @return	An associative array containing Room Variables' values, where the key is the variable name.
     *
     * @example	<code>
     * 			let roomVars:Array = room.getVariables()
     *
     * 			for (let v:String in roomVars)
     * 				trace("Name:" + v + " | Value:" + roomVars[v])
     * 			</code>
     *
     * @see		#getVariable
     * @see		SmartFoxClient#setRoomVariables
     *
     * @version	SmartFoxServer Basic / Pro
     */
    public getVariables(): any[] {
        return this.variables;
    }

    /**
     * Set the Room Variables.
     *
     * @param	vars:	an array of Room Variables.
     *
     * @exclude
     */
    public setVariables(vars: any[]): void {
        this.variables = vars;
    }

    /**
     * Reset Room Variables.
     *
     * @exclude
     */
    public clearVariables(): void {
        this.variables = [];
    }

    /**
     * Get the name of the room.
     *
     * @return	The name of the room.
     *
     * @example	<code>
     * 			trace("Room name:" + room.getName())
     * 			</code>
     *
     * @see		#getId
     *
     * @version	SmartFoxServer Basic / Pro
     */
    public getName(): string {
        return this.name;
    }

    /**
     * Get the id of the room.
     *
     * @return	The id of the room.
     *
     * @example	<code>
     * 			trace("Room id:" + room.getId())
     * 			</code>
     *
     * @see		#getName
     *
     * @version	SmartFoxServer Basic / Pro
     */
    public getId(): number {
        return this.id;
    }

    /**
     * A boolean flag indicating if the room is dynamic/temporary.
     * This is always true for rooms created at runtime on client-side.
     *
     * @return	{@code true} if the room is a dynamic/temporary room.
     *
     * @example	<code>
     * 			if (room.isTemp)
     * 				trace("Room is temporary")
     * 			</code>
     *
     * @version	SmartFoxServer Basic / Pro
     */
    public isTemp(): boolean {
        return this.temp;
    }

    /**
     * A boolean flag indicating if the room is a "game room".
     *
     * @return	{@code true} if the room is a "game room".
     *
     * @example	<code>
     * 			if (room.isGame)
     * 				trace("This is a game room")
     * 			</code>
     *
     * @see		#isLimbo
     *
     * @version	SmartFoxServer Basic / Pro
     */
    public isGame(): boolean {
        return this.game;
    }

    /**
     * A boolean flag indicating if the room is private (password protected).
     *
     * @return	{@code true} if the room is private.
     *
     * @example	<code>
     * 			if (room.isPrivate)
     * 				trace("Password required for this room")
     * 			</code>
     *
     * @version	SmartFoxServer Basic / Pro
     */
    public isPrivate(): boolean {
        return this.priv;
    }

    /**
     * Retrieve the number of users currently inside the room.
     *
     * @return	The number of users in the room.
     *
     * @example	<code>
     * 			let usersNum:int = room.getUserCount()
     * 			trace("There are " + usersNum + " users in the room")
     * 			</code>
     *
     * @see		#getSpectatorCount
     *
     * @version	SmartFoxServer Basic / Pro
     */
    public getUserCount(): number {
        return this.userCount;
    }

    /**
     * Retrieve the number of spectators currently inside the room.
     *
     * @return	The number of spectators in the room.
     *
     * @example	<code>
     * 			let specsNum:int = room.getSpectatorCount()
     * 			trace("There are " + specsNum + " spectators in the room")
     * 			</code>
     *
     * @see		#getUserCount
     *
     * @version	SmartFoxServer Basic / Pro
     */
    public getSpectatorCount(): number {
        return this.specCount;
    }

    /**
     * Retrieve the maximum number of users that can join the room.
     *
     * @return	The maximum number of users that can join the room.
     *
     * @example	<code>
     * 			trace("Max users allowed to join the room: " + room.getMaxUsers())
     * 			</code>
     *
     * @see		#getMaxSpectators
     *
     * @version	SmartFoxServer Basic / Pro
     */
    public getMaxUsers(): number {
        return this.maxUsers;
    }

    /**
     * Retrieve the maximum number of spectators that can join the room.
     * Spectators can exist in game rooms only.
     *
     * @return	The maximum number of spectators that can join the room.
     *
     * @example	<code>
     * 			if (room.isGame)
     * 				trace("Max spectators allowed to join the room: " + room.getMaxSpectators())
     * 			</code>
     *
     * @see		#getMaxUsers
     *
     * @version	SmartFoxServer Basic / Pro
     */
    public getMaxSpectators(): number {
        return this.maxSpectators;
    }


    /**
     * Set the myPlayerId property.
     * Each room where the current client is connected contains a myPlayerId (if the room is a gameRoom).
     * myPlayerId == -1 ... user is a spectator
     * myPlayerId  > 0  ...	user is a player
     *
     * @param	id:	the myPlayerId value.
     *
     * @exclude
     */
    public setMyPlayerIndex(id: number): void {
        this.myPlayerIndex = id;
    }

    /**
     * Retrieve the player id for the current user in the room.
     * This id is 1-based (player 1, player 2, etc.), but if the user is a spectator its value is -1.
     *
     * @return	The player id for the current user.
     *
     * @example	<code>
     * 			if (room.isGame)
     * 				trace("My player id in this room: " + room.getMyPlayerIndex())
     * 			</code>
     *
     * @version	SmartFoxServer Basic / Pro
     */
    public getMyPlayerIndex(): number {
        return this.myPlayerIndex;
    }

    /**
     * Set the {@link #isLimbo} property.
     *
     * @param	b:	{@code true} if the room is a "limbo room".
     *
     * @exclude
     */
    public setIsLimbo(b: boolean): void {
        this.limbo = b;
    }

    /**
     * A boolean flag indicating if the room is in "limbo mode".
     *
     * @return	{@code true} if the room is in "limbo mode".
     *
     * @example	<code>
     * 			if (room.isLimbo)
     * 				trace("This is a limbo room")
     * 			</code>
     *
     * @see		#isGame
     *
     * @version	SmartFoxServer Basic / Pro
     */
    public isLimbo(): boolean {
        return this.limbo;
    }

    /**
     * Se the number of users in the room.
     *
     * @param	n:	the number of users.
     *
     * @exclude
     */
    public setUserCount(n: number): void {
        this.userCount = n;
    }

    /**
     * Se the number of spectators in the room.
     *
     * @param	n:	the number of spectators.
     *
     * @exclude
     */
    public setSpectatorCount(n: number): void {
        this.specCount = n;
    }

    /**
    * @exclude
    */
    public setUserList(uList: any[]): void {
        this.userList = uList;
    }
}

